import React from 'react'

import { useNavigate } from 'react-router'
import styled, { CSS } from 'styled-components'

import { ResponsiveImage } from '@atoms/index'
import { Paragraph, Heading, Button, Spacer, getPageSectionButtonColor } from '@client/components'
import { DeviceContainer } from '@client/components/utility'
import { useConfig } from '@client/contexts/ConfigProvider'
import { ContainerBorderRadius, getObjectFit, MediumBoxShadow, ResponsivePXValue, StrongBoxShadow } from '@components/Theme'
import { PageBannerFragment } from '@hooks/api'

const Container = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: center;
  justify-content: center;

  ${ResponsivePXValue('padding', { mobile: '28px 0', tablet: '32px 0', desktop: '75px 0' })}

  .full-width {
    width: 100%;
  }
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1;
  background-color: rgba(256,256,256,0.7);

  ${MediumBoxShadow}
  ${ContainerBorderRadius}

  ${ResponsivePXValue('padding', { mobile: '32px 10px', tablet: '25px 15px', desktop: '40px' })}
  ${ResponsivePXValue('width', { mobile: '80%', tablet: '65%', desktop: '60%' })}
  ${ResponsivePXValue('max-height', { mobile: '150px' })}
`

const ImageContainer = styled.div<{ $imageShadow?: boolean }>`

  display: flex;
  position: absolute;

  ${(props): CSS => (props.$imageShadow) ? StrongBoxShadow : null}
`
export interface PageBannerElegantProps {
  pageBanner: PageBannerFragment
  height?: string
  mobileHeight?: string
}

export function PageBannerElegant({ pageBanner }: PageBannerElegantProps): JSX.Element {

  const navigate = useNavigate()
  const config = useConfig()

  const linkUrl = pageBanner?.link?.link
  const linkTitle = pageBanner?.link?.title
  const linkColor = getPageSectionButtonColor(pageBanner?.link?.color)
  const description = pageBanner?.description
  const subTitle = pageBanner?.subTitle

  const objectFit = getObjectFit(pageBanner)

  const _handleLinkClick = (e?: React.MouseEvent<HTMLAnchorElement>): void => {
    e.preventDefault()
    e.stopPropagation()

    if (linkUrl) {
      if (linkUrl.indexOf('http') !== -1 && config.isBrowser()) {
        window.location.href = linkUrl
      } else {
        navigate(linkUrl)
      }
    }
  }

  return (
    <Container>
      <ImageContainer $imageShadow={pageBanner.imageShadow}>
        <DeviceContainer $mobile className='full-width'>
          <ResponsiveImage image={pageBanner.mobileImage} objectFit={objectFit} />
        </DeviceContainer>
        <DeviceContainer $desktop $tablet className='full-width'>
          <ResponsiveImage image={pageBanner.image} objectFit={objectFit} />
        </DeviceContainer>
      </ImageContainer>
      <ContentContainer>
        <Choose>
          <When condition={!!description && !!subTitle}>
            <Heading variant='h5' align='center' color={pageBanner.titleColor}>{pageBanner.bannerTitle}</Heading>
            <Spacer universal='24px'/>
            <Heading variant='h3' align='center' color={pageBanner.subTitleColor} fontWeight={500}>{pageBanner.subTitle}</Heading>
            <Spacer universal='24px' />
            <Paragraph bold align='center' variant='p4' color={pageBanner.descriptionColor} className='description'>{description} </Paragraph>
          </When>
          <When condition={!!description && !subTitle}>
            <Heading variant='h2' align='center' color={pageBanner.titleColor}>{pageBanner.bannerTitle}</Heading>
            <Spacer universal='24px' />
            <Paragraph bold align='center' variant='p4' color={pageBanner.descriptionColor} className='description'>{description} </Paragraph>
          </When>
          <Otherwise>
            <Heading variant='h3' align='center' color={pageBanner.titleColor}>{pageBanner.bannerTitle}</Heading>
            <Spacer universal='24px' />
            <Heading variant='h2' align='center' color={pageBanner.subTitleColor}>{pageBanner.subTitle}</Heading>
          </Otherwise>
        </Choose>
        <If condition={!!linkTitle}>
          <Spacer universal='24px' />
          <Button title={linkTitle} href={linkUrl} color={linkColor} onClick={_handleLinkClick} />
        </If>
      </ContentContainer>
    </Container>
  )
}
